import { useState, useEffect, useCallback } from 'react';

interface UseTypewriterOptions {
  text: string;
  typingSpeed?: number;
  pauseDuration?: number;
  loop?: boolean;
}

interface UseTypewriterReturn {
  displayedText: string;
  isTyping: boolean;
  isComplete: boolean;
  cursorVisible: boolean;
}

export function useTypewriter({
  text,
  typingSpeed = 10,
  pauseDuration = 5000,
  loop = true,
}: UseTypewriterOptions): UseTypewriterReturn {
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [isComplete, setIsComplete] = useState(false);
  const [cursorVisible, setCursorVisible] = useState(true);

  const resetAndRestart = useCallback(() => {
    setDisplayedText('');
    setIsTyping(true);
    setIsComplete(false);
  }, []);

  // Typing effect
  useEffect(() => {
    if (!isTyping) return;

    if (displayedText.length < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(text.slice(0, displayedText.length + 1));
      }, typingSpeed);
      return () => clearTimeout(timeout);
    } else {
      setIsTyping(false);
      setIsComplete(true);
    }
  }, [displayedText, isTyping, text, typingSpeed]);

  // Pause and restart loop
  useEffect(() => {
    if (!isComplete || !loop) return;

    const timeout = setTimeout(() => {
      resetAndRestart();
    }, pauseDuration);

    return () => clearTimeout(timeout);
  }, [isComplete, loop, pauseDuration, resetAndRestart]);

  // Blinking cursor
  useEffect(() => {
    const interval = setInterval(() => {
      setCursorVisible((prev) => !prev);
    }, 530);

    return () => clearInterval(interval);
  }, []);

  return {
    displayedText,
    isTyping,
    isComplete,
    cursorVisible,
  };
}
